import { useCallback, useEffect, useMemo, useRef } from "react"
import { truncateString } from "@/v2-common/utils/utils.formatting"
import Tooltip from "@/v2-ui/tooltip"
import ViewSheet from "@/v2-backoffice-client/components/viewSheet"
import AppViewSheetBreadcrumb from "@/v2-backoffice-client/app/AppViewSheetBreadcrumb"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import Dot from "@/v2-backoffice-client/components/dot/Dot"
import InfoGrid, {
  createInfoGridFields,
} from "@/v2-backoffice-client/components/infoGrid"
import UptimeHistoryBar from "@/v2-backoffice-client/uptime/UptimeHistoryBar"
import UptimeViewHeader from "@/v2-backoffice-client/uptime/view/UptimeViewHeader"
import UptimeViewErrorCard from "@/v2-backoffice-client/uptime/view/UptimeViewErrorCard"
import UptimeViewLatencyStats from "@/v2-backoffice-client/uptime/view/UptimeViewLatencyStats"
import UptimeViewAvailability from "@/v2-backoffice-client/uptime/view/UptimeViewAvailability"
import UptimeViewIncidents from "@/v2-backoffice-client/uptime/view/UptimeViewIncidents"
import UptimeViewDriftSection from "@/v2-backoffice-client/uptime/view/UptimeViewDriftSection"
import UptimeViewResponseChart from "@/v2-backoffice-client/uptime/view/UptimeViewResponseChart"
import UptimeEditPublicSection from "@/v2-backoffice-client/uptime/editPublic/UptimeEditPublicSection"
import UptimeMuteButton from "@/v2-backoffice-client/uptime/mute/UptimeMuteButton"
import UptimeMuteBadge from "@/v2-backoffice-client/uptime/mute/UptimeMuteBadge"
import { useUptimeLiveProbe } from "@/v2-backoffice-client/uptime/view/runProbe/useUptimeLiveProbe"
import {
  getCategoryLabel,
  getHostFromUrl,
  getPlatform,
  getPlatformLabel,
  getProviderName,
  getStatusVariant,
} from "@/v2-backoffice-client/uptime/uptime.utils"
import { useUptimeViewDetailQuery } from "@/v2-backoffice-client/uptime/view/detail/uptime.view.detail.query"
import type { UptimeTargetFragment } from "@/v2-backoffice-client/uptime/__types__/uptime.fragment"
import UptimeTargetLink from "@/v2-backoffice-client/uptime/view/UptimeTargetLink"

// Header InfoGrid -- mirrors the new uptime-table columns (Category,
// Source, Host, Protocol, URL) so the operator sees the same
// vocabulary they were just scanning in the row, plus a clickable
// URL. Identifier is included as the last field so the same grid
// also exposes the underlying key for engineers debugging via the
// gql cache.
const HEADER_FIELDS = createInfoGridFields<UptimeTargetFragment>({
  fields: {
    Platform: {
      label: "Platform",
      accessor: (t) => getPlatformLabel(getPlatform(t)),
    },
    Category: {
      label: "Category",
      accessor: (t) => getCategoryLabel(t.Source),
    },
    Source: {
      label: "Source",
      accessor: (t) => getProviderName(t),
    },
    Host: {
      label: "Host",
      accessor: (t) => getHostFromUrl(t.Url) || null,
      renderValue({ value }) {
        if (typeof value !== "string" || !value) return null
        return <span className="font-mono text-xs">{value}</span>
      },
    },
    Protocol: {
      label: "Protocol",
      accessor: "Protocol",
      renderValue({ value }) {
        if (typeof value !== "string" || !value) return null
        return <span className="font-mono text-xs uppercase">{value}</span>
      },
    },
    Identifier: {
      label: "Identifier",
      accessor: "Identifier",
      defaultHidden: true,
      renderValue({ value }) {
        if (typeof value !== "string" || !value) return null
        return <span className="font-mono text-xs break-all">{value}</span>
      },
    },
    Url: {
      label: "URL",
      accessor: "Url",
      to: ({ value }) => (typeof value === "string" ? value : undefined),
      renderValue({ value }) {
        if (typeof value !== "string" || !value) return null
        // return <span className="font-mono text-xs break-all">{value}</span>
        return truncateString(String(value), 30, "...")
      },
    },
  },
})

function parseKey(id: string | null | undefined): {
  source: string
  identifier: string
} | null {
  if (!id) return null
  const pipe = id.indexOf("|")
  if (pipe === -1) return null
  return {
    source: id.slice(0, pipe),
    identifier: id.slice(pipe + 1),
  }
}

function UptimeViewSheet() {
  const { id } = useAppViewSheetOpener()
  const key = useMemo(() => parseKey(id), [id])

  // Stable refs so the live-probe hook can call into both refetch
  // handles without us having to recreate the live-probe callback
  // (and tear down the underlying timer) on every render.
  const chartRefetchRef = useRef<(() => void) | null>(null)
  const refetchViewRef = useRef<(() => void) | null>(null)

  const liveState = useUptimeLiveProbe({
    enabled: Boolean(key),
    source: key?.source ?? "",
    identifier: key?.identifier ?? "",
    onProbe: useCallback(() => {
      refetchViewRef.current?.()
      chartRefetchRef.current?.()
    }, []),
  })

  // Pause the 60 s detail-query poll while the live loop is
  // actively scheduling probes. The probe mutation already returns
  // the full UptimeTargetFragment so Apollo's normalised cache
  // keeps the sheet fresh; doubling up with a periodic poll just
  // burns network round-trips.
  const liveActive = liveState.nextProbeAt != null

  const {
    data,
    loading,
    error,
    refetch: refetchView,
  } = useUptimeViewDetailQuery({
    skip: !key,
    variables: key
      ? { input: { source: key.source, identifier: key.identifier } }
      : undefined,
    pollInterval: liveActive ? 0 : 60_000,
  })

  // Keep the refetch ref in sync with whichever Apollo handle the
  // latest render produced -- the live-probe callback above is
  // stable across renders so it always reads through this ref.
  useEffect(() => {
    refetchViewRef.current = refetchView
  }, [refetchView])

  const target = data?.getUptimeTarget
  const availability = data?.getUptimeTargetAvailability ?? []
  const incidents = data?.getUptimeTargetIncidents ?? []

  if (!key) {
    return (
      <>
        <ViewSheet.Header>
          <AppViewSheetBreadcrumb />
          <ViewSheet.Title>Uptime target</ViewSheet.Title>
        </ViewSheet.Header>
        <div className="p-4 text-sm text-destructive">
          Invalid uptime target key.
        </div>
      </>
    )
  }

  return (
    <Tooltip.Provider delayDuration={150}>
      <ViewSheet.Header className="uptime-view-sheet-header">
        {/* Top row: breadcrumb stays anchored on the left while the
            dot + title + muted badge cluster sits on the right edge,
            so an operator scanning the sheet header sees the most
            important identification (status colour + name) at a
            glance without having to read the breadcrumb first. The
            row wraps gracefully on narrower sheets via flex-wrap. */}
        <div className="uptime-view-sheet-top-row">
          <div className="uptime-view-sheet-breadcrumb-slot">
            <AppViewSheetBreadcrumb />
          </div>
          <div className="uptime-view-sheet-title-row">
            {target && <Dot variant={getStatusVariant(target.LatestStatus)} />}
            <ViewSheet.Title
              isLoading={loading && !target}
              className="uptime-view-sheet-title"
            >
              <span className="truncate">{target?.Name ?? key.identifier}</span>
            </ViewSheet.Title>
            {target && <UptimeMuteBadge target={target} size="md" />}
          </div>
        </div>
      </ViewSheet.Header>

      {target && (
        <ViewSheet.HeaderRow>
          {/* InfoGrid replaces the old custom meta block. Same fields
              the table now exposes (Category / Source / Host /
              Protocol / URL) so the sheet header speaks the same
              vocabulary the operator just clicked through, with a
              clickable URL and a hidden-by-default Identifier for
              engineers. */}
          <InfoGrid
            id="uptime-target-info"
            data={target}
            fields={HEADER_FIELDS}
          />
          <ViewSheet.Toolbar>
            <UptimeMuteButton target={target} />
            <UptimeTargetLink target={target} />
          </ViewSheet.Toolbar>
        </ViewSheet.HeaderRow>
      )}

      {error && (
        <div className="p-5 text-sm text-destructive">
          Failed to load uptime detail: {error.message}
        </div>
      )}

      {target && (
        <div className="uptime-view-sheet-body">
          {/* Stat cards: current-streak duration, last check, 90d
              down-count. Lives at the very top so the most-asked
              questions are answered first. */}
          <UptimeViewHeader target={target} />

          {/* Error card: prominent red block while currently down,
              neutral block surfacing the most-recent failure when
              currently up. Rendered only when there's something to
              show. */}
          <UptimeViewErrorCard target={target} />

          <section className="uptime-view-chart-frame">
            <div className="uptime-view-section-header">
              <div className="uptime-view-section-title">90-day history</div>
              <div className="uptime-view-section-aside">
                {target.UptimePercent90d.toFixed(2)}% uptime · last 90 days
              </div>
            </div>
            <UptimeHistoryBar history={target.DailyHistory} />
          </section>

          <UptimeViewLatencyStats target={target} />

          <UptimeViewResponseChart
            source={target.Source}
            identifier={target.Identifier}
            baselineP95Ms={target.P95DurationMs90d}
            baselineP50Ms={target.P50DurationMs90d}
            liveState={liveState}
            refetchRef={chartRefetchRef}
          />

          <section className="flex flex-col gap-3">
            <div className="uptime-view-section-header">
              <div className="uptime-view-section-title">Availability</div>
              <div className="uptime-view-section-aside">
                today · 7 d · 30 d · 365 d · all
              </div>
            </div>
            <UptimeViewAvailability rows={availability} />
          </section>

          <section className="flex flex-col gap-3">
            <div className="uptime-view-section-header">
              <div className="uptime-view-section-title">Incidents</div>
              <div className="uptime-view-section-aside">last 90 days</div>
            </div>
            <UptimeViewIncidents incidents={incidents} />
          </section>

          {/*
            Drift section. Renders nothing for targets that don't
            participate in metadata fingerprinting (e.g. plain HTTP
            healthchecks); for layer / metadata-aware sources it
            embeds the same per-field drift table the standalone
            Layer view sheet uses, keyed by source+identifier.
          */}
          <UptimeViewDriftSection target={target} />

          {/*
            Edit public details. Last because the operator workflow
            is "triage -> verify metrics -> tweak public copy", not
            "tweak public copy first". The section is collapsed by
            default and only mounts the config query lazily so a
            normal triage pass pays nothing extra for it.
          */}
          <UptimeEditPublicSection target={target} />
        </div>
      )}
    </Tooltip.Provider>
  )
}

export default UptimeViewSheet
